<template>
  <v-container class="external-changes-list">
    <v-row justify="center">
      <ViewHeader :title="$t('message.externalChanges.list.title')" />
      <v-col cols="12">
        <v-card class="external-changes-list__card">
          <v-container>
            <v-row>
              <v-col cols="12">
                <div class="d-flex align-end">
                  <div class="full-width">
                    <v-text-field
                      v-model="facilitiesSearchInputValue"
                      class="search-field"
                      :label="
                        $t('message.externalChanges.list.searchPlaceholder')
                      "
                      prepend-inner-icon="icon-search"
                      clearable
                      outlined
                      rounded
                      :hide-details="true"
                      @input="onFacilitySearch"
                    ></v-text-field>
                  </div>
                  <ui-button
                    color="primary"
                    @click="filterDialog = true"
                    class="ml-2"
                    icon="icon-filter"
                  >
                    {{ $t("message.common.filter") }}
                  </ui-button>
                </div>
              </v-col>
              <v-col cols="12" class="pb-0">
                <div class="d-flex flex-column">
                  <Pagination
                    :page="page"
                    :per-page="perPage"
                    :options="perPageOptions"
                    :total-count="totalCount"
                    :show-if-empty="true"
                    @change="handlePageChange"
                  >
                    <div class="d-flex align-center">
                      <ExternalChangesSourceSelect
                        class="mr-5"
                        :loading="loading.list"
                        @update="handleSourceUpdate"
                      />
                      <div
                        class="pr-2"
                        v-if="canAcceptAndRejectExternalChanges"
                      >
                        <v-menu offset-y>
                          <template v-slot:activator="{ on, attrs }">
                            <ui-button
                              color="primary"
                              v-bind="attrs"
                              v-on="on"
                              :disabled="
                                selectedFacilitiesIds.length === 0 ||
                                  isSelectedFacilitiesCountOverLimit
                              "
                              append-icon="icon-arrow-down"
                            >
                              {{ $t("message.common.actions") }}
                            </ui-button>
                          </template>
                          <v-list class="actions__list">
                            <v-list-item
                              v-for="action in actionsTypes"
                              :key="action.id"
                              class="actions__list-item"
                              @click="handleGroupAction(action.type)"
                            >
                              <v-list-item-title>
                                {{ action.text }}
                              </v-list-item-title>
                            </v-list-item>
                          </v-list>
                        </v-menu>
                        <v-tooltip
                          v-if="isSelectedFacilitiesCountOverLimit"
                          top
                        >
                          <template v-slot:activator="{ on }">
                            <v-icon color="accent" v-on="on" class="ml-2">
                              mdi-information
                            </v-icon>
                          </template>
                          <span>{{
                            $t(
                              "message.externalChanges.list.overLimitTooltip",
                              { count: selectedFacilitiesCountLimit }
                            )
                          }}</span>
                        </v-tooltip>
                      </div>
                    </div>
                  </Pagination>

                  <div
                    class="external-changes-list__select-wrapper"
                    v-if="
                      loaded &&
                        currentPageFacilities.length > 0 &&
                        canAcceptAndRejectExternalChanges
                    "
                  >
                    <div class="d-flex">
                      <v-checkbox
                        :input-value="isAllFacilitiesSelected"
                        :label="$t('message.common.selectAll')"
                        @change="toggleAllFacilities"
                        class="mr-4"
                      ></v-checkbox>
                      <v-checkbox
                        :input-value="isAllCurrentPageFacilitiesSelected"
                        :label="$t('message.common.selectAllFromPage')"
                        :disabled="isAllFacilitiesSelected"
                        @change="toggleAllCurrentPageFacilities"
                      ></v-checkbox>
                    </div>

                    <ItemsCounter
                      v-if="selectedFacilitiesCount"
                      :count="selectedFacilitiesCount"
                    />
                  </div>
                </div>
              </v-col>
              <v-col
                class="external-changes-list__list-wrapper"
                v-if="loaded && currentPageFacilities.length > 0"
              >
                <div class="external-changes-list__list">
                  <v-expansion-panels v-model="panel">
                    <ExternalChangeLocationItem
                      v-for="item in currentPageFacilities"
                      :key="item.id"
                      :item="item"
                      :source="changeSource"
                      @toggleFacility="toggleFacility"
                      @reload="fetchFacilities"
                      @close="panel = null"
                    />
                  </v-expansion-panels>
                </div>

                <SectionLoader :status="loading.list" />
              </v-col>
              <v-col v-else-if="loaded && currentPageFacilities.length === 0">
                <div class="no-changes">
                  <img
                    src="@/static/externalChanges/noChanges.svg"
                    :alt="$t('message.externalChanges.noChangesAlt')"
                  />
                  <p class="no-changes__description">
                    {{ $t("message.externalChanges.noChanges") }}
                  </p>
                </div>
              </v-col>
            </v-row>
            <SectionLoader :status="loading.view" />
          </v-container>
        </v-card>
      </v-col>

      <FilterDialog
        v-if="filterDialog"
        :show-open-status="true"
        :cities="cities"
        :facilities-ids="facilitiesIds"
        :facilities-groups="facilitiesGroupsIds"
        :chosen-search-type="searchType"
        :chosen-open-status="status.open"
        @setFacilitiesIds="facilitiesIds = $event"
        @setCities="cities = $event"
        @setFacilitiesGroupsIds="facilitiesGroupsIds = $event"
        @setSearchType="searchType = $event"
        @setOpenStatus="status.open = $event"
        @change="handleFiltersChange({ ...$event })"
        :handleClose="closeFilterDialog"
      />

      <GroupChangeDialog
        :is-open="groupChangeDialog"
        :chosen-action="chosenAction"
        :chosen-source="changeSource"
        :selected-facilities-count="selectedFacilitiesCount"
        @close="handleCloseGroupChangeDialog"
        @save="handleGroupChange"
      />
    </v-row>
  </v-container>
</template>

<script>
import { mapState, mapGetters, mapActions } from "vuex";
import cuid from "cuid";

import { debounce } from "@/helpers/helpers";
import { permissionCheck } from "@/helpers/permission-check";
import ErrorHandler from "@/helpers/handlers/error";
import gmbExternalChangesService from "@/services/externalChanges";
import { Permission } from "@/enums/permission";
import { EXTERNAL_CHANGES_STATUS } from "@/enums/external-changes";
import { GTM_EVENT_NAME } from "@/enums/gtm-events";
import { gtmEngagement as gtmEngagementMixin } from "@/helpers/mixins/gtm-engagement";
import { gtmEvent__utmEmailNotification } from "@/helpers/gtm-events";
import { setCompanyIdFromQuery as setCompanyIdFromQueryMixin } from "@/helpers/mixins/set-company-id-from-query";
import { isUtmTaggedUrl } from "@/helpers/utm-tags";

import ViewHeader from "@/components/ui/ViewHeader.vue";
import SectionLoader from "@/components/Loader/SectionLoader.vue";
import ItemsCounter from "@/components/List/ItemsCounter";
import Pagination from "@/components/List/Pagination";
import FilterDialog from "@/components/Filter/dialog";
import ExternalChangeLocationItem from "@/components/ExternalChanges/ExternalChangeLocationItem.vue";
import GroupChangeDialog from "@/components/ExternalChanges/dialog/GroupChangeDialog.vue";
import ExternalChangesSourceSelect from "@/components/ExternalChanges/ExternalChangesSourceSelect.vue";

export default {
  name: "ExternalChangesList",
  components: {
    ViewHeader,
    SectionLoader,
    ItemsCounter,
    Pagination,
    FilterDialog,
    ExternalChangeLocationItem,
    GroupChangeDialog,
    ExternalChangesSourceSelect
  },
  mixins: [gtmEngagementMixin, setCompanyIdFromQueryMixin],
  data() {
    return {
      page: 1,
      perPage: 10,
      perPageOptions: [10, 50, 100, 200, 500],
      panel: null,
      loaded: false,
      loading: {
        view: false,
        list: false
      },
      actionsTypes: [
        {
          type: EXTERNAL_CHANGES_STATUS.ACCEPTED,
          text: this.$t("message.common.accept"),
          id: cuid()
        },
        {
          type: EXTERNAL_CHANGES_STATUS.REJECTED,
          text: this.$t("message.common.reject"),
          id: cuid()
        }
      ],
      facilitiesSearchInputValue: "",
      filterDialog: false,
      cities: [],
      facilitiesIds: [],
      facilitiesGroupsIds: [],
      status: {
        open: 0
      },
      changeSource: null,
      searchType: null,
      groupChangeDialog: false,
      chosenAction: null,
      selectedFacilitiesCountLimit: 50
    };
  },

  computed: {
    ...mapGetters({
      isAllFacilitiesSelected: "facilities/isAllFacilitiesSelected",
      isAllCurrentPageFacilitiesSelected:
        "facilities/isAllCurrentPageFacilitiesSelected",
      selectedFacilitiesIds: "facilities/selectedFacilitiesIds",
      selectedFacilitiesCount: "facilities/selectedFacilitiesCount"
    }),
    ...mapState({
      companyId: state => state.company.id,
      currentPageFacilities: state => state.facilities.currentPageFacilities,
      totalCount: state => state.facilities.totalCount
    }),
    canAcceptAndRejectExternalChanges() {
      return permissionCheck(Permission.APPROVE_AND_REJECT_EXTERNAL_CHANGES);
    },
    isSelectedFacilitiesCountOverLimit() {
      return this.selectedFacilitiesCount > this.selectedFacilitiesCountLimit;
    }
  },

  async created() {
    this.loading.view = true;
    await this.initFacilities({
      perPage: this.perPage,
      externalChangesStatuses: [EXTERNAL_CHANGES_STATUS.PENDING]
    });
    this.loaded = true;
    this.loading.view = false;
  },

  mounted() {
    if (isUtmTaggedUrl(this.$route.query)) {
      gtmEvent__utmEmailNotification(this.$route.query);
    }

    this.mixin__setGtmEngagementTimeouts();
  },

  destroyed() {
    this.resetFacilitiesStore();
    this.resetFacilitiesGroupsStore();
  },

  methods: {
    ...mapActions({
      resetFacilitiesStore: "facilities/resetStore",
      resetFacilitiesGroupsStore: "facilitiesGroups/resetStore",
      initFacilities: "facilities/initFacilities",
      selectFacilities: "facilities/selectFacilities",
      toggleFacility: "facilities/toggleFacility",
      toggleAllFacilities: "facilities/toggleAllFacilities",
      toggleAllCurrentPageFacilities:
        "facilities/toggleAllCurrentPageFacilities",
      exportAndDownloadSelectedFacilities:
        "facilities/exportAndDownloadSelectedFacilities"
    }),
    async fetchFacilities() {
      this.loading.list = true;
      this.panel = null;

      const params = {
        page: this.page,
        perPage: this.perPage,
        query: this.facilitiesSearchInputValue,
        isOwner: true,
        externalChangesStatuses: [EXTERNAL_CHANGES_STATUS.PENDING],
        externalChangesSources: [this.changeSource],
        cities: this.cities,
        facilitiesIds: this.facilitiesIds,
        groups: this.facilitiesGroupsIds,
        openStatus: this.status.open,
        fetchAllFacilitiesIds: true
      };

      await this.$store.dispatch("facilities/fetchFacilities", params);
      this.loading.list = false;
    },

    async handlePageChange({ page, perPage }) {
      this.page = page;
      this.perPage = perPage;
      await this.fetchFacilities();
    },

    async handleFiltersChange({
      facilitiesIds = this.facilitiesIds,
      cities = this.cities,
      facilitiesGroupsIds = this.facilitiesGroupsIds,
      openStatus = this.status.open
    }) {
      this.page = 1;

      this.facilitiesIds = facilitiesIds;
      this.cities = cities;
      this.facilitiesGroupsIds = facilitiesGroupsIds;
      this.status.open = openStatus;

      await this.fetchFacilities();
    },

    closeFilterDialog() {
      this.selectFacilities([]);
      this.filterDialog = false;
    },

    onFacilitySearch: debounce(async function() {
      this.page = 1;
      await this.fetchFacilities();
    }, 500),

    handleGroupAction(type) {
      this.chosenAction = type;
      this.groupChangeDialog = true;
    },

    handleCloseGroupChangeDialog() {
      this.chosenAction = null;
      this.groupChangeDialog = false;
    },

    async handleGroupChange({ fields }) {
      this.loading.list = true;

      // GTM event data collection below
      const action =
        this.chosenAction === EXTERNAL_CHANGES_STATUS.ACCEPTED
          ? "accept"
          : this.chosenAction === EXTERNAL_CHANGES_STATUS.REJECTED
          ? "reject"
          : null;

      try {
        this.$gtm.trackCustomEvent({
          event: GTM_EVENT_NAME.EXTERNAL_CHANGES_MASS_ACTION_SUMMARY,
          action,
          num_locations: this.selectedFacilitiesIds
            ? this.selectedFacilitiesIds.length
            : null,
          num_selected: fields ? fields.length : null
        });

        fields.forEach(field => {
          this.$gtm.trackCustomEvent({
            event: GTM_EVENT_NAME.EXTERNAL_CHANGES_MASS_ACTION_DETAILS,
            action,
            num_locations: this.selectedFacilitiesIds
              ? this.selectedFacilitiesIds.length
              : null,
            field
          });
        });
        // end GTM event stuff

        const result = await gmbExternalChangesService.groupUpdateExternalChanges(
          {
            facilitiesIds: this.selectedFacilitiesIds,
            fields,
            status: this.chosenAction,
            source: this.changeSource
          }
        );

        if (result.status === 204) {
          this.loaded = false;
          this.loading.view = true;

          this.selectFacilities([]);
          await this.fetchFacilities();
          this.loaded = true;
          this.loading.view = false;
          this.$store.dispatch("externalChanges/fetchProtectionSummary");
        }
      } catch (error) {
        // GTM event data collection below
        if (error && error.response && error.response.status === 400) {
          this.$gtm.trackCustomEvent({
            event: GTM_EVENT_NAME.LOCATION_PROCESSING_IN_PROGRESS,
            num_locations: this.selectedFacilitiesIds
              ? this.selectedFacilitiesIds.length
              : null,
            action: `bulk_ext_change_${action}`
          });
        }
        // end GTM event stuff

        ErrorHandler.displayError(error);
      }

      this.loading.list = false;
      this.handleCloseGroupChangeDialog();
    },

    handleSourceUpdate(source) {
      this.changeSource = source;
      this.handleFiltersChange({});
    }
  }
};
</script>

<style lang="scss" scoped>
.external-changes-list {
  &__card {
    min-height: 650px;
  }

  &__select-wrapper {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-left: $spacing;
  }

  &__list-wrapper {
    position: relative;
  }

  &__list {
    max-height: 628px;
    overflow-x: visible;
    overflow-y: auto;
  }
}

.actions {
  &__list {
    padding: 0;
    border-radius: 25px;
    overflow: hidden;
    min-width: 170px;
    @include font-size(14px);
  }

  &__list-item {
    padding: 0 $spacing * 3;

    &:nth-child(2n + 1) {
      background-color: $light-4;
    }

    & > div {
      @include font-size(14px);
    }
  }
}

.v-expansion-panels {
  margin-bottom: 0;

  &:not(:last-child) {
    border-bottom: 1px solid $light-3;
  }
}

.no-changes {
  @include noData(650px);

  &__description {
    @include font-size(22px);
    text-align: center;
    margin: $spacing * 5 0 $spacing * 3;
  }
}
</style>
